import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { TASK_BUDGET_FIELDS } from "@/other/constants/OtherKeys";
import { useEntityFinderContext } from "../context/EntityFinderContext";
import { TaskBudget } from "@/bryntum-lib/models/TaskBudget";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { getFontSize } from "@/react/context/bryntumContext";

export const BudgetTab = () => {
  const { data, handleClose } = useEntityFinderContext();
  const { editorHandler } = useSwivuBryntumContext();
  const budgets = data as TaskBudget[];
  const containerRef = useRef<HTMLDivElement>(null);

  const gap = 12;
  const rowVirtualizer = useVirtualizer({
    count: budgets.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => getFontSize() * 3 + gap,
    overscan: 8
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleBudgetClick = (budget: TaskBudget) => {
    // @ts-expect-error openEditor is not in the type definition
    editorHandler.openEditor(EDITOR_KEYS.budgetEditor, budget, { budget }, { by: "recordType", useExisting: true });
    handleClose();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-auto bg-background">
        {budgets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <svg className="w-12 h-12 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm">No budgets found</p>
          </div>
        ) : (
          <div className="relative w-full" style={{ height: `${totalSize}px` }}>
            {virtualItems.map((virtualRow) => {
              const budget = budgets[virtualRow.index];
              return (
                <div
                  key={budget.id}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <div
                    className="px-4 flex items-center bg-card hover:opacity-90 cursor-pointer transition-all rounded-md"
                    style={{ height: `${virtualRow.size - gap}px`, marginBottom: `${gap}px` }}
                    onClick={() => handleBudgetClick(budget)}
                  >
                    <div className="font-medium text-foreground line-clamp-1">{budget[TASK_BUDGET_FIELDS.title] || "N/A"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
